import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import {
  Ticket,
  Search,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Loader2,
  X,
  Save,
  UserCheck
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SupportTicket {
  id: string;
  conversation_id: string;
  user_id: string | null;
  session_id: string | null;
  user_message: string;
  user_email: string | null;
  user_name: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assigned_to: string | null;
  admin_notes: string | null;
  resolved_at: string | null;
  webhook_sent: boolean;
  webhook_sent_at: string | null;
  webhook_error: string | null;
  created_at: string;
  updated_at: string;
}

interface AdminUser {
  id: string;
  email: string;
}

type StatusFilter = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';

interface AdminChatbotSupportProps {
  noLayout?: boolean;
}

export function AdminChatbotSupport({ noLayout = false }: AdminChatbotSupportProps = {}) {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    total: 0,
  });

  useEffect(() => {
    fetchTickets();
    fetchAdmins();
  }, [statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('chatbot_support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTickets(data || []);

      const allTicketsQuery = await supabase
        .from('chatbot_support_tickets')
        .select('status');

      const allTickets = allTicketsQuery.data || [];

      setStats({
        open: allTickets.filter(t => t.status === 'open').length,
        in_progress: allTickets.filter(t => t.status === 'in_progress').length,
        resolved: allTickets.filter(t => t.status === 'resolved').length,
        closed: allTickets.filter(t => t.status === 'closed').length,
        total: allTickets.length,
      });

    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email')
        .eq('is_admin', true);

      if (error) throw error;

      setAdmins(data || []);
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  };

  const openTicketModal = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setAdminNotes(ticket.admin_notes || '');
    setAssignedTo(ticket.assigned_to || '');
  };

  const closeTicketModal = () => {
    setSelectedTicket(null);
    setAdminNotes('');
    setAssignedTo('');
  };

  const handleUpdateTicket = async (newStatus?: 'open' | 'in_progress' | 'resolved' | 'closed') => {
    if (!selectedTicket) return;

    setSaving(true);
    try {
      const updates: any = {
        admin_notes: adminNotes || null,
        assigned_to: assignedTo || null,
      };

      if (newStatus) {
        updates.status = newStatus;
        if (newStatus === 'resolved' || newStatus === 'closed') {
          updates.resolved_at = new Date().toISOString();
        }
      }

      const { error } = await supabase
        .from('chatbot_support_tickets')
        .update(updates)
        .eq('id', selectedTicket.id);

      if (error) throw error;

      fetchTickets();
      closeTicketModal();

    } catch (err) {
      console.error('Error updating ticket:', err);
      alert('Fout bij opslaan van ticket');
    } finally {
      setSaving(false);
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.user_message.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'resolved':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in_progress':
        return 'In behandeling';
      case 'resolved':
        return 'Opgelost';
      case 'closed':
        return 'Gesloten';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const content = (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chatbot Support Tickets</h1>
          <p className="text-gray-600 mt-1">Beheer support verzoeken vanuit de chatbot</p>
        </div>

        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">Totaal</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Open</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.open}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">In behandeling</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Opgelost</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">Gesloten</span>
            </div>
            <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek in tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alles ({stats.total})
              </button>
              <button
                onClick={() => setStatusFilter('open')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'open'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Open ({stats.open})
              </button>
              <button
                onClick={() => setStatusFilter('in_progress')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'in_progress'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                In behandeling ({stats.in_progress})
              </button>
              <button
                onClick={() => setStatusFilter('resolved')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'resolved'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Opgelost ({stats.resolved})
              </button>
              <button
                onClick={() => setStatusFilter('closed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'closed'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Gesloten ({stats.closed})
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="text-gray-500 mt-2">Laden...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-12 text-center">
              <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen tickets gevonden</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => openTicketModal(ticket)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(ticket.status)}`}>
                          {getStatusLabel(ticket.status)}
                        </span>
                        {ticket.user_id && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                            Ingelogd
                          </span>
                        )}
                        {ticket.assigned_to && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                            <UserCheck className="w-3 h-3" />
                            Toegewezen
                          </span>
                        )}
                        {ticket.webhook_sent ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            Mail verstuurd
                          </span>
                        ) : ticket.webhook_error ? (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Mail mislukt
                          </span>
                        ) : null}
                      </div>

                      <p className="text-sm text-gray-900 font-medium mb-2 line-clamp-2">
                        {ticket.user_message}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        {ticket.user_name && (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.user_name}
                          </div>
                        )}
                        {ticket.user_email && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {ticket.user_email}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(ticket.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Support Ticket</h2>
                <button
                  onClick={closeTicketModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded ${getStatusColor(selectedTicket.status)}`}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bericht van gebruiker</label>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-900">
                  {selectedTicket.user_message}
                </div>
              </div>

              {(selectedTicket.user_name || selectedTicket.user_email) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedTicket.user_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Naam</label>
                      <p className="text-sm text-gray-900">{selectedTicket.user_name}</p>
                    </div>
                  )}
                  {selectedTicket.user_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <p className="text-sm text-gray-900">{selectedTicket.user_email}</p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Toewijzen aan</label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Niet toegewezen</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin notities</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Interne notities over dit ticket..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mail Status</label>
                {selectedTicket.webhook_sent ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-green-700 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Mail verstuurd</span>
                    </div>
                    {selectedTicket.webhook_sent_at && (
                      <p className="text-xs text-green-600 ml-6">
                        Verzonden: {formatDate(selectedTicket.webhook_sent_at)}
                      </p>
                    )}
                  </div>
                ) : selectedTicket.webhook_error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-red-700 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Mail mislukt</span>
                    </div>
                    <p className="text-xs text-red-600 mb-3 ml-6 font-mono">
                      {selectedTicket.webhook_error}
                    </p>
                    {selectedTicket.user_email && (
                      <a
                        href={`mailto:${selectedTicket.user_email}`}
                        className="ml-6 text-xs text-blue-600 hover:text-blue-700 underline"
                      >
                        Mail klant zelf
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Geen mailstatus bekend</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tijden</label>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Aangemaakt: {formatDate(selectedTicket.created_at)}</p>
                  <p>Laatst bijgewerkt: {formatDate(selectedTicket.updated_at)}</p>
                  {selectedTicket.resolved_at && (
                    <p>Opgelost: {formatDate(selectedTicket.resolved_at)}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => handleUpdateTicket()}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Opslaan
                  </>
                )}
              </button>

              {selectedTicket.status === 'open' && (
                <button
                  onClick={() => handleUpdateTicket('in_progress')}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Start behandeling
                </button>
              )}

              {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                <button
                  onClick={() => handleUpdateTicket('resolved')}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  Markeer als opgelost
                </button>
              )}

              {selectedTicket.status === 'resolved' && (
                <button
                  onClick={() => handleUpdateTicket('closed')}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Sluit ticket
                </button>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
  );

  if (noLayout) {
    return content;
  }

  return <AdminLayout>{content}</AdminLayout>;
}
